import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertTriangle, Package, ShieldCheck, Truck } from "lucide-react";
import SEO from "@/components/SEO";
import { useCart } from "@/context/CartContext";
import { formatGBP } from "@/lib/formatGBP";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30;

function maskEmail(email) {
  if (!email) return "***";
  const [local, domain] = email.split("@");
  if (!domain) return "***";
  return `${local.slice(0, 2)}***@${domain}`;
}

export default function ThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderNum = params.get("order");
  const { clearCart } = useCart();

  const [order, setOrder] = useState(null);
  const [phase, setPhase] = useState("polling"); // polling | paid | failed | not_found
  const attemptsRef = useRef(0);
  const cartClearedRef = useRef(false);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setPhase("not_found");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      setOrder(res.data);
      // Treat both `paid` and `dispatched` as terminal-success states so that
      // customers revisiting the thank-you link after their order has been
      // dispatched still resolve to the confirmed view (instead of hanging
      // on the polling spinner).
      if (res.data.status === "paid" || res.data.status === "dispatched") {
        setPhase("paid");
        if (!cartClearedRef.current) {
          clearCart();
          cartClearedRef.current = true;
        }
        return;
      }
      if (res.data.status === "failed") {
        setPhase("failed");
        return;
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setPhase("not_found");
        return;
      }
      // transient — keep polling
    }
    attemptsRef.current += 1;
    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
      setPhase("failed");
    }
  }, [sessionId, clearCart]);

  useEffect(() => {
    if (!sessionId) {
      setPhase("not_found");
      return undefined;
    }
    poll();
    const id = setInterval(() => {
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        clearInterval(id);
        return;
      }
      // stop polling on terminal states
      if (phase === "paid" || phase === "failed" || phase === "not_found") {
        clearInterval(id);
        return;
      }
      poll();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, phase]);

  return (
    <>
      <SEO
        title={phase === "paid" ? "Order confirmed | PeptiChill" : "Confirming your order | PeptiChill"}
        description="Your PeptiChill pre-order has been confirmed. Ships July 2026, UK tracked delivery."
        path="/thank-you"
      />
      <section className="bg-ice-light py-16 lg:py-24 min-h-[70vh]" data-testid="page-thank-you">
        <div className="container-pc">
          {phase === "polling" && <PollingState />}
          {phase === "paid" && order && <PaidState order={order} />}
          {(phase === "failed" || phase === "not_found") && (
            <FailedState orderNumber={orderNum} />
          )}
        </div>
      </section>
    </>
  );
}

function PollingState() {
  return (
    <div
      className="max-w-md mx-auto text-center bg-white border border-silver/40 shadow-card rounded-2xl p-10"
      data-testid="thank-you-polling"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-ice flex items-center justify-center">
        <Loader2 size={22} className="text-navy animate-spin" />
      </div>
      <h1 className="mt-6 text-2xl font-bold text-navy tracking-tight">Confirming your order…</h1>
      <p className="mt-3 text-sm text-muted leading-relaxed">
        Just a moment while we confirm your payment with Stripe. This usually takes a few seconds.
      </p>
    </div>
  );
}

function PaidState({ order }) {
  return (
    <div className="max-w-2xl mx-auto" data-testid="thank-you-paid">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-cyan-soft/40 border border-cyan flex items-center justify-center">
          <CheckCircle2 size={28} className="text-navy" strokeWidth={2.2} />
        </div>
        <h1 className="mt-6 text-4xl sm:text-5xl font-bold text-navy tracking-tightest leading-[1.05]">
          Thank you, {order.customer_first_name}.
        </h1>
        <p className="mt-4 text-base text-muted">Your pre-order is confirmed.</p>
        <p
          className="mt-6 inline-block rounded-full bg-white border border-silver/60 px-5 py-2 text-sm text-ink"
          data-testid="thank-you-order-number"
        >
          Order <strong className="text-navy">{order.order_number}</strong>
        </p>
      </div>

      <div className="mt-10 rounded-2xl bg-white border border-silver/40 shadow-card p-6">
        <h2 className="text-base font-bold text-navy">Order summary</h2>
        <ul className="mt-5 space-y-4 divide-y divide-silver/40">
          {order.items.map((it) => (
            <li key={it.name} className="flex gap-4 pt-4 first:pt-0">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-ice-light border border-silver/40 shrink-0">
                {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-navy leading-tight">{it.name}</p>
                <p className="text-xs text-muted mt-0.5">Qty {it.quantity}</p>
              </div>
              <p className="text-sm font-semibold text-navy tabular-nums">
                {formatGBP(it.price_pennies * it.quantity)}
              </p>
            </li>
          ))}
        </ul>
        <div className="mt-5 pt-5 border-t border-silver/40 flex justify-between items-center">
          <span className="font-semibold text-navy">Total paid</span>
          <span className="text-xl font-bold text-navy tabular-nums">
            {formatGBP(order.amount_pennies)}
          </span>
        </div>
      </div>

      <p className="mt-6 text-center text-sm text-muted" data-testid="thank-you-email-note">
        You&rsquo;ll receive a confirmation email shortly at{" "}
        <span className="font-medium text-navy">
          {order.customer_email_masked || maskEmail("")}
        </span>
        .
      </p>

      <div className="mt-10 rounded-2xl bg-white border border-silver/40 shadow-card p-6">
        <h3 className="text-sm font-bold text-navy uppercase tracking-widest">What happens next</h3>
        <ol className="mt-4 space-y-3 text-sm text-ink">
          <li className="flex gap-3">
            <Step n="1" /> Pre-order confirmed.
          </li>
          <li className="flex gap-3">
            <Step n="2" /> PeptiChill ships from July 2026.
          </li>
          <li className="flex gap-3">
            <Step n="3" /> Tracked delivery 2–3 working days from dispatch.
          </li>
        </ol>
      </div>

      <ul className="mt-8 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-muted">
        <li className="inline-flex items-center gap-1.5">
          <ShieldCheck size={14} className="text-cyan" /> Secure payment via Stripe
        </li>
        <li className="inline-flex items-center gap-1.5">
          <Package size={14} className="text-cyan" /> UK tracked delivery
        </li>
        <li className="inline-flex items-center gap-1.5">
          <Truck size={14} className="text-cyan" /> 2–3 working days
        </li>
      </ul>

      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-full bg-navy text-white px-7 py-3 text-sm font-semibold hover:brightness-110 transition shadow-card"
          data-testid="thank-you-home"
        >
          Return to home
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center justify-center rounded-full border border-navy text-navy px-7 py-3 text-sm font-semibold hover:bg-ice transition"
          data-testid="thank-you-contact"
        >
          Contact us
        </Link>
      </div>
    </div>
  );
}

function Step({ n }) {
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-navy text-cyan text-xs font-bold shrink-0">
      {n}
    </span>
  );
}

function FailedState({ orderNumber }) {
  return (
    <div
      className="max-w-md mx-auto text-center bg-white border border-silver/40 shadow-card rounded-2xl p-10"
      data-testid="thank-you-failed"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
        <AlertTriangle size={22} className="text-destructive" />
      </div>
      <h1 className="mt-6 text-2xl font-bold text-navy tracking-tight">
        We couldn&rsquo;t confirm your payment
      </h1>
      <p className="mt-3 text-sm text-muted leading-relaxed">
        It looks like the payment didn&rsquo;t complete. Your basket has been saved — please try again.
      </p>
      {orderNumber && (
        <p className="mt-3 text-xs text-muted">
          If your card was charged, contact{" "}
          <a href="mailto:hello@peptidecooler.co.uk" className="text-navy underline">
            hello@peptidecooler.co.uk
          </a>{" "}
          with order number <strong className="text-navy">{orderNumber}</strong>.
        </p>
      )}
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <Link
          to="/cart"
          className="inline-flex items-center justify-center rounded-full bg-navy text-white px-6 py-2.5 text-sm font-semibold hover:brightness-110 transition"
          data-testid="thank-you-failed-retry"
        >
          Try again
        </Link>
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-full border border-navy text-navy px-6 py-2.5 text-sm font-semibold hover:bg-ice transition"
        >
          Return to home
        </Link>
      </div>
    </div>
  );
}
